/**
 * The client-only **"Always run"** setting: a single checkbox in the Settings panel
 * (`#alwaysrun-checkbox` in `index.html`) that forces the run gait for all manual
 * steering - so the player runs **without holding Shift** (keyboard) and **with the
 * cursor at any distance** past the turn zone (mouse), instead of only on Shift / a
 * far-out cursor (the `RUN_RADIUS_PX` threshold in `src/input/mouseMove.ts`).
 *
 * Purely a client-side movement preference: it persists in its own `britbank.alwaysRun`
 * localStorage key and touches no socket. `running` is already a client-owned gait flag
 * on every `Walk` request (Shift never leaves the browser, and `handleWalk` never gates
 * on it), so this changes only *when* the client sends a run - no wire, protocol, or
 * server change (ADR-0243).
 *
 * Like `blood.ts`/`music.ts`/`settings.ts`, this module owns **only** the DOM wiring;
 * the markup lives in `index.html`, and it touches no Pixi, so it is unit-testable
 * headlessly (jsdom). The movement ticker in `main.ts` reads the live choice each frame
 * via {@link AlwaysRunHandle.isEnabled}. Default **off** (walk unless Shift / a far
 * cursor) when nothing is stored - opt-in, the UO convention.
 */

/** Local-storage key the player's "Always run" choice persists under. */
const ALWAYS_RUN_KEY = 'britbank.alwaysRun';

/** The window owning a document (the live `window` in the app, the jsdom one in tests). */
function windowOf(doc: Document): Window & typeof globalThis {
  return (doc.defaultView ?? window) as Window & typeof globalThis;
}

/**
 * Read the saved "Always run" choice, defaulting to **off** (walk unless Shift / a far
 * cursor) when nothing is stored, the value is unrecognised, or storage is unavailable.
 */
function loadAlwaysRun(doc: Document): boolean {
  try {
    // Only an explicit '1' turns always-run on; anything else (incl. absent) is off.
    return windowOf(doc).localStorage.getItem(ALWAYS_RUN_KEY) === '1';
  } catch {
    return false;
  }
}

/** Persist the "Always run" choice so it survives a reload/reconnect. */
function saveAlwaysRun(doc: Document, on: boolean): void {
  try {
    windowOf(doc).localStorage.setItem(ALWAYS_RUN_KEY, on ? '1' : '0');
  } catch {
    /* storage unavailable (private mode / disabled); the choice is session-only. */
  }
}

/** Options for {@link initAlwaysRun}; all optional - `doc` is injected by tests. */
export interface AlwaysRunOptions {
  /**
   * Called once at startup with the persisted choice and again on every toggle, for a
   * caller that wants to react to the change. The movement ticker instead reads
   * {@link AlwaysRunHandle.isEnabled} live each frame, so this is optional.
   */
  onChange?: (on: boolean) => void;
  /** The document to wire (defaults to the live `document`); injected by tests. */
  doc?: Document;
}

/** The handle `main.ts`/tests drive the "Always run" setting through. */
export interface AlwaysRunHandle {
  /** Whether always-run is currently on (read live by the movement ticker each frame). */
  isEnabled(): boolean;
  /** Set the "Always run" state programmatically (reflects, persists, and notifies). */
  setEnabled(on: boolean): void;
}

export function initAlwaysRun(opts: AlwaysRunOptions = {}): AlwaysRunHandle {
  const doc = opts.doc ?? document;
  const checkbox = doc.getElementById('alwaysrun-checkbox') as HTMLInputElement | null;

  let on = loadAlwaysRun(doc);

  /** Paint the checkbox to match the current state. */
  const reflect = (): void => {
    if (checkbox) checkbox.checked = on;
  };

  /** Move the choice to `next`, optionally persisting, then reflect + notify. */
  const apply = (next: boolean, persist: boolean): void => {
    on = next;
    if (persist) saveAlwaysRun(doc, on);
    reflect();
    opts.onChange?.(on);
  };

  checkbox?.addEventListener('change', () => apply(checkbox.checked, true));

  // Paint the saved state up front (and notify any listener), so a player who turned
  // always-run on last session sees it checked and runs from their first step.
  reflect();
  opts.onChange?.(on);

  return {
    isEnabled: () => on,
    setEnabled: (next) => apply(next, true),
  };
}
